import * as React from 'react';
import { Link, useParams } from 'react-router-dom';
import { useMutation } from '@tanstack/react-query';
import { GoDecision } from '@clawhire/shared';
import { useAuth } from '@/auth/AuthProvider';
import { api } from '@/lib/api';
import { Button } from '@/components/ui/button';
import { EmptyState } from '@/components/ui/empty-state';
import { PageHeader } from '@/components/ui/page-header';
import { Skeleton, SkeletonLines } from '@/components/ui/skeleton';
import { toast } from '@/components/ui/toaster';
import { ActivityTimeline } from '@/components/data/ActivityTimeline';
import { EnumBadge } from '@/components/data/EnumBadge';
import { FilesPanel } from '@/components/data/FilesPanel';
import { FollowUpList } from '@/components/data/FollowUpList';
import { Banner } from '../components/Banner';
import { FlagBadges, PortalBadge } from '../components/PositionBadges';
import { SectionCard } from '../components/SectionCard';
import { useInvalidatePositions, usePosition } from '../queries';
import type { PositionDetail as PositionDetailData } from '../types';
import { StageMenu, useStageChange } from '../useStageChange';
import { BriefingsSection } from './BriefingsSection';
import { ContactBlock } from './ContactBlock';
import { DescriptionSection } from './DescriptionSection';
import { JdPanel } from './JdPanel';
import { PortalCheckForm } from './PortalCheckForm';
import { PositionProperties } from './PositionProperties';
import { RateBlock } from './RateBlock';
import { SubmissionsSection } from './SubmissionsSection';

/** `/positions/:id` — loads the detail and renders the page (or a not-found state). */
export function PositionDetail() {
  const { id = '' } = useParams();
  const query = usePosition(id);

  if (query.isLoading) {
    return (
      <>
        <PageHeader
          breadcrumbs={[{ label: 'Positions', to: '/positions' }, { label: id }]}
          title={<Skeleton className="h-7 w-72" />}
        />
        <div className="grid gap-6 lg:grid-cols-[minmax(0,1fr)_22rem]" aria-busy="true">
          <SkeletonLines lines={8} />
          <SkeletonLines lines={5} />
        </div>
      </>
    );
  }
  if (query.error || !query.data) {
    return (
      <>
        <PageHeader
          breadcrumbs={[{ label: 'Positions', to: '/positions' }, { label: id }]}
          title="Position"
        />
        <EmptyState
          title="Position not found"
          description={query.error?.message ?? 'This position does not exist or was deleted.'}
          action={
            <Button asChild variant="outline">
              <Link to="/positions">Back to positions</Link>
            </Button>
          }
        />
      </>
    );
  }
  return <PositionDetailView position={query.data} />;
}

function PositionDetailView({ position }: { position: PositionDetailData }) {
  const { canWrite, isAdmin } = useAuth();
  const invalidate = useInvalidatePositions();

  const update = useMutation({
    mutationFn: (patch: Record<string, unknown>) =>
      api.patch<PositionDetailData>(`/positions/${encodeURIComponent(position.id)}`, patch),
    onSuccess: () => invalidate(),
    onError: (e: Error) => toast.error(e.message),
  });
  const { mutateAsync: patchAsync } = update;
  const onPatch = React.useCallback((patch: Record<string, unknown>) => patchAsync(patch), [patchAsync]);

  const stage = useStageChange();
  const { computed } = position;
  const description = [
    position.requestNo,
    position.intermediary?.name ?? null,
    position.jdReference,
    position.clientInternalName ? `${position.clientInternalName} (internal)` : null,
  ]
    .filter(Boolean)
    .join(' · ');

  return (
    <>
      <PageHeader
        breadcrumbs={[{ label: 'Positions', to: '/positions' }, { label: position.requestNo }]}
        title={position.role}
        description={description}
        meta={
          <>
            <StageMenu
              stage={position.stage}
              onSelect={(s) => void stage.change(position.id, s)}
              disabled={!canWrite || stage.isPending}
            />
            <PortalBadge position={position} />
            <FlagBadges computed={computed} />
            <EnumBadge def={GoDecision} value={position.goDecision} />
          </>
        }
        actions={canWrite ? <PortalCheckForm position={position} /> : null}
      />

      {computed.onSiteBlocked || position.restrictions || computed.closedOnPortalButActive ? (
        <div className="mb-6 space-y-2">
          {computed.onSiteBlocked ? (
            <Banner tone="danger" title="Regular on-site presence – not taken on; JD generation blocked">
              {position.goReason && position.goDecision !== 'GO' ? position.goReason : null}
            </Banner>
          ) : null}
          {position.restrictions ? (
            <Banner tone="warning" title="Restrictions">
              {position.restrictions}
            </Banner>
          ) : null}
          {computed.closedOnPortalButActive ? (
            <Banner tone="info" title="Closed on the portal but still active">
              The request no longer accepts new CVs; submissions already in the client process can continue.
              Move the position to Lost or Dropped once the client decides.
            </Banner>
          ) : null}
        </div>
      ) : null}

      <div className="grid gap-6 lg:grid-cols-[minmax(0,1fr)_22rem]">
        <div className="min-w-0 space-y-6">
          <RateBlock position={position} canEdit={canWrite} onPatch={onPatch} />
          <PositionProperties position={position} canEdit={canWrite} onPatch={onPatch} />
          <ContactBlock position={position} canEdit={canWrite} onPatch={onPatch} />
          <DescriptionSection position={position} canEdit={canWrite} onPatch={onPatch} />
          <JdPanel position={position} canEdit={canWrite} isAdmin={isAdmin} />
          <BriefingsSection position={position} canEdit={canWrite} />
          <SubmissionsSection position={position} canEdit={canWrite} />
        </div>
        <aside className="min-w-0 space-y-6" aria-label="Side panel">
          <SectionCard title="Activity">
            <ActivityTimeline
              entityType="POSITION"
              entityId={position.id}
              entries={position.activity}
              canAdd={canWrite}
              onChanged={invalidate}
            />
          </SectionCard>
          <SectionCard title="Follow-ups">
            <FollowUpList
              entityType="POSITION"
              entityId={position.id}
              items={position.followUps}
              canEdit={canWrite}
              onChanged={invalidate}
            />
          </SectionCard>
          <SectionCard title="Files">
            <FilesPanel
              entityType="POSITION"
              entityId={position.id}
              files={position.files}
              canEdit={canWrite}
              defaultKind="OTHER"
              uploadKinds={['OTHER', 'JD_PDF', 'JD_DOCX', 'OPEN_JD_COPY']}
              onChanged={invalidate}
            />
          </SectionCard>
        </aside>
      </div>

      {stage.dialog}
    </>
  );
}
